/** Map API errors onto react-hook-form fields (ARCHITECT §6 error shape → inline messages). */

import type { FieldValues, Path, UseFormSetError } from "react-hook-form";

import { isApiError } from "@motolynk/api-client";

/**
 * Puts each `errors[field]` message on the matching form field and returns the message that
 * belongs to the whole form (non-field errors or the error message), or null.
 */
export function applyServerErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fields: readonly Path<T>[],
): string | null {
  if (!isApiError(error)) return error instanceof Error ? error.message : "Something went wrong.";
  let placed = false;
  for (const field of fields) {
    const message = error.fieldError(field);
    if (message) {
      setError(field, { type: "server", message });
      placed = true;
    }
  }
  const formLevel = error.errors.non_field_errors?.[0];
  if (formLevel) return formLevel;
  return placed ? null : error.message;
}

/**
 * Like `applyServerErrors`, for forms with lists: every field error goes onto its path as the API names it
 * ("title", "variants.1.price", "attributes.voltage") — the same paths React Hook Form uses.
 */
export function applyServerErrorPaths<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
): string | null {
  if (!isApiError(error)) return error instanceof Error ? error.message : "Something went wrong.";
  let placed = false;
  for (const [field, messages] of Object.entries(error.errors)) {
    if (field === "non_field_errors" || !messages?.length) continue;
    setError(field as Path<T>, { type: "server", message: messages[0] });
    placed = true;
  }
  const formLevel = error.errors.non_field_errors?.[0];
  if (formLevel) return formLevel;
  return placed ? null : error.message;
}
